/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { reqIdCompNameTable, wizardComponents } from "./ComponentsMap";

const PARAM = {
  ID: "active_test_id",
  ID_REQUIREMENT: "requirement_id",
};

export default {
  name: "activeTestControlPanel",
  data() {
    return {
      currentWizard: "", // 表示コンポーネント名
    };
  },

  props: {
    // 選択されたAT項目
    selectedAtFunc: { type: Object, default: "", required: false },
    // 選択項目名称i18nキー
    i18nMenuTitleKey: { type: String, default: "", required: false },
    // 駆動中フラグ
    isDriving: { type: Boolean, required: true },
    // DM駆動中フラグ
    inExecution: { type: Boolean, required: true },
    // DM開始時刻
    baseTime: { type: Number, default: null, required: false },
    // DM項目選択画面表示フラグ
    showDmSelect: { type: Boolean, required: false },
    // DMエラー発生フラグ
    isDmError: { type: Boolean, required: false },
  },

  components: {
    ...wizardComponents, // コントロールパネルのコンポーネント
  },

  watch: {
    selectedAtFunc: {
      handler() {
        this.selectComponent(this.requirementId);
      },
    },
  },

  computed: {
    requirementId() {
      return this.selectedAtFunc[PARAM.ID_REQUIREMENT];
    },
  },

  methods: {
    /** componentのmounted時に呼び出す処理 */
    mountedLogic() {
      this.selectComponent(this.requirementId);
    },

    /** 状態切替（駆動開始/停止状態） */
    toggleDrive(flg) {
      this.$emit("toggle-drive", flg);
    },

    /** 状態切替（計測開始/停止状態） */
    toggleExecute(flg) {
      this.$emit("toggle-execute", flg);
    },

    /** 状態切替（データ読み出し実行フラグ） */
    toggleReadExecuted(flg) {
      this.$emit("toggle-read-executed", flg);
    },

    /** コンポーネント独自のisDrivingフラグ変更した際のイベント処理 */
    notifyChangeDrive(flg) {
      this.$emit("notify-change-drive", flg);
    },

    /** コントロールパネル切替処理 */
    selectComponent(reqId) {
      const componentName = this.getComponentName(reqId);
      this.currentWizard = componentName;
    },

    /** 選択項目と対応するコントロールパネル名の取得 */
    getComponentName(reqId) {
      let res = "";
      // 要求ID対応表でコンポーネント名取得
      Object.keys(reqIdCompNameTable).find((key) => {
        if (reqIdCompNameTable[key].some((e) => e === reqId)) res = key;
      });
      return res;
    },

    /** AT失敗時の駆動停止処理 */
    errTerminate() {
      this.$emit("err-terminate");
    },

    /** AT失敗時のDM停止・画面遷移処理 */
    atFailure() {
      this.$emit('at-failure');
    },

    /** 
     * データモニタ停止処理
     * @param {function} callback モニタ停止完了後に呼び出されるコールバック関数  (SP7専用)
     */
    async stopMonitor(callback) {
      await this.$emit('stop-monitor', callback);
    },

    /** 
     * 現在のウィザードコンポーネントを取得する
     * @returns {Promise} 現在のウィザードコンポーネント
     */
    getCurrentWizard() {
      const self = this;
      return new Promise((r) => {
        if (self.$refs.current_wizard) {
          r(self.$refs.current_wizard);
          return;
        }
        const timeoutHandler = () => {
          if (self.$refs.current_wizard) {
            r(self.$refs.current_wizard);
            return;
          }
          setTimeout(timeoutHandler, 500);
        };
        setTimeout(timeoutHandler, 500);
      });
    },

    /** 
     * AT実行画面のAT文言を削除する
     * 子コンポーネントは、このメソッドを実装する必要がある。
     */
    clearAtMsg() {
      if (this.$refs.current_wizard) {
        this.$refs.current_wizard.clearAtMsg();
      }
    },

    /** 
     * サインアウト処理 
     */
    setSignout() {
      if (this.$refs.current_wizard) {
        this.$refs.current_wizard.setSignout();
      }
    },

    /**
     * AT項目選択ボタンクリック時の処理
     * 子コンポーネントは、このメソッドを実装する必要がある。
     */
    async btnAtSelectOnClicked() {
      (await this.getCurrentWizard()).btnAtSelectOnClicked();
    },

    /**
     * 駆動状態を取得する
     */
    checkDriving() {
      const self = this;
      if (!self.$refs.current_wizard) {
        return this.isDriving;
      }
      const current = self.$refs.current_wizard;
      if ((!current.checkDriving) || ((typeof current.checkDriving) !== "function")) {
        return this.isDriving;
      }
      return current.checkDriving();
    },

    /**
     * キャンセル処理
     * @param {object} exec - 実行情報
     */
    async cancelExec(exec) {
      if (!exec) {
        return;
      }
      const func = (await this.getCurrentWizard()).cancelExec;
      if ((func !== undefined) && (func !== null) &&
        (typeof func === 'function')) {
        await func();
      }
    },

    /** 
     * HEV系AT: IO/Routine Control項目を空欄に設定する
     */
    dmChangeToBlank() {
      this.$emit("dm-change-to-blank");
    },

    /**
     * 本コンポーネントのクリア
     */
    clear() {
      // 指定コンポーネントもクリアして、子コンポーネントも消す
      this.currentWizard = "";
    },

    /**
     * アクティブテスト用のマーカ情報の保存
     * @param {object} markInfo マーカー登録情報
     * @returns {boolean} 処理の成否
     */
    async saveActiveTestExecuteMark(markInfo) {
      return await this.$emit("save-active-test-executeMark", markInfo);
    },

    /** 
     * SP2:データモニタ停止前にアクティブテストを停止する
     */
    async stopDrive() {
      const func = (await this.getCurrentWizard()).stopDrive;
      if ((func !== undefined) && (func !== null) &&
        (typeof func === 'function')) {
        await func();
      }
    },

  },

  mounted() {
    this.mountedLogic();
  },
};
